import {gql} from '@apollo/client';
import React, {useEffect} from 'react';

import {AntibodyNumberingLayout} from 'constants/MapsConstants';
import {
  AntibodyNumberingChainType,
  AntibodyNumberingScheme,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {AaSequencePart} from 'molbio/aa-sequences/util/AaSequenceConstants';
import {
  AaSequencePartsAntibodyNumberingQuery,
  AaSequencePartsAntibodyNumberingQueryVariables,
} from 'molbio/maps/linear/__generated/AaSequencePartsAntibodyNumberingQuery';
import {
  AaSequenceVersionAntibodyNumberingQuery,
  AaSequenceVersionAntibodyNumberingQueryVariables,
} from 'molbio/maps/linear/__generated/AaSequenceVersionAntibodyNumberingQuery';
import {
  AntibodyNumberingTrack,
  getNumberingLayerHeight,
  ResidueNumbering,
} from 'molbio/maps/linear/AntibodyNumbering';
import {getFeatureLayersForRegionModels} from 'molbio/maps/linear/LayeringHelpers';
import {capitalize} from 'util/NameUtil';

export const AA_SEQUENCE_VERSION_ANTIBODY_NUMBERING_QUERY = gql`
  query AaSequenceVersionAntibodyNumberingQuery(
    $aaSequenceVersionSpecifiers: [AaSequenceVersionSpecifierInput!]!
  ) @skipAudit @engComponent(name: ANTIBODIES) {
    aaSequenceVersions(aaSequenceVersionSpecifiers: $aaSequenceVersionSpecifiers) {
      ... on AaSequenceVersionNode {
        dbId
        aminoAcids
        antibodyNumberingInfo {
          numbering
          numberingScheme
          organism
          chainType
          sourceAllele
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const AA_SEQUENCE_PARTS_ANTIBODY_NUMBERING_QUERY = gql`
  query AaSequencePartsAntibodyNumberingQuery($ids: [StrictString!]!)
  @skipAudit
  @engComponent(name: ANTIBODIES) {
    aaSequences(ids: $ids) {
      ... on AaSequenceNode {
        id
        displayLabel
        experimentalCurrentVersion {
          dbId
          aminoAcids
          antibodyNumberingInfo {
            numbering
            numberingScheme
            organism
            chainType
            sourceAllele
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const SCHEME_DISPLAY_NAMES: Record<AntibodyNumberingScheme, string> = {
  [AntibodyNumberingScheme.KABAT]: 'Kabat',
  [AntibodyNumberingScheme.IMGT]: 'IMGT',
  [AntibodyNumberingScheme.EU]: 'EU',
};

const CHAIN_TYPE_DISPLAY_NAMES: Record<AntibodyNumberingChainType, string> = {
  [AntibodyNumberingChainType.HEAVY]: 'Heavy',
  [AntibodyNumberingChainType.KAPPA]: 'Kappa',
  [AntibodyNumberingChainType.LAMBDA]: 'Lambda',
  [AntibodyNumberingChainType.ALPHA]: 'Alpha',
  [AntibodyNumberingChainType.BETA]: 'Beta',
  [AntibodyNumberingChainType.GAMMA]: 'Gamma',
  [AntibodyNumberingChainType.DELTA]: 'Delta',
};

interface NumberingInfo {
  numberingScheme: AntibodyNumberingScheme;
  organism: string;
  chainType: AntibodyNumberingChainType;
  sourceAllele: string | null;
}

/**
 * Returns the summary shown on hover over stored numbering, e.g. "IMGT · Human Heavy · IGHV3-23*01".
 */
function getNumberingSummary({numberingScheme, organism, chainType, sourceAllele}: NumberingInfo): string {
  return [
    SCHEME_DISPLAY_NAMES[numberingScheme],
    `${capitalize(organism)} ${CHAIN_TYPE_DISPLAY_NAMES[chainType]}`,
    sourceAllele,
  ]
    .filter((part) => part != null)
    .join(' · ');
}

/**
 * Returns the stored numbering of the queried version, or null if it has none or it doesn't fit the
 * residues being drawn (e.g. while residue edits are unsaved).
 */
export function getStoredResidueNumbering(
  data: AaSequenceVersionAntibodyNumberingQuery,
  residues: string
): ResidueNumbering | null {
  const [version] = data.aaSequenceVersions;
  if (version?.__typename !== 'AaSequenceVersionNode' || version.antibodyNumberingInfo == null) {
    return null;
  }
  const {numbering} = version.antibodyNumberingInfo;
  if (version.aminoAcids.toUpperCase() !== residues.toUpperCase() || numbering.length !== residues.length) {
    return null;
  }
  return {start: 0, labels: numbering, summary: getNumberingSummary(version.antibodyNumberingInfo)};
}

/**
 * Returns the antibody numbering stored on an AA sequence version, or null while it loads, if the
 * version has none, or if it doesn't fit the residues being drawn.
 */
export function useStoredResidueNumbering(params: {
  aaSequenceId: string;
  aaSequenceVersionId: number;
  residues: string;
  skip?: boolean;
}): ResidueNumbering | null {
  return useVersionNumbering(params).numbering;
}

/**
 * Returns whether the version's stored numbering has loaded, and the numbering if the version has one that
 * fits the residues being drawn.
 */
function useVersionNumbering({
  aaSequenceId,
  aaSequenceVersionId,
  residues,
  skip = false,
}: {
  aaSequenceId: string;
  aaSequenceVersionId: number;
  residues: string;
  skip?: boolean;
}): {loaded: boolean; numbering: ResidueNumbering | null} {
  const result = useSkippableBenchlingQuery<
    AaSequenceVersionAntibodyNumberingQuery,
    AaSequenceVersionAntibodyNumberingQueryVariables
  >(AA_SEQUENCE_VERSION_ANTIBODY_NUMBERING_QUERY, {
    skip,
    variables: {aaSequenceVersionSpecifiers: [{aaSequenceId, versionDbId: aaSequenceVersionId}]},
  });
  return result.type === QueryResultType.SUCCESS
    ? {loaded: true, numbering: getStoredResidueNumbering(result.data, residues)}
    : {loaded: false, numbering: null};
}

/**
 * Returns the stored numbering of the current version of each part's sequence, placed at the part, in the
 * order of the parts. Skips parts whose sequence is inaccessible, has no numbering, or is out of sync.
 */
export function getPartResidueNumberings(
  data: AaSequencePartsAntibodyNumberingQuery,
  parts: ReadonlyArray<AaSequencePart>,
  residues: string
): Array<ResidueNumbering> {
  return parts.flatMap((part) => {
    const sequence = data.aaSequences.find(
      (node) => node.__typename === 'AaSequenceNode' && node.id === part.proteinId
    );
    if (sequence?.__typename !== 'AaSequenceNode') {
      return [];
    }
    const {aminoAcids, antibodyNumberingInfo} = sequence.experimentalCurrentVersion;
    if (antibodyNumberingInfo == null) {
      return [];
    }
    const end = part.end === 0 ? residues.length : part.end;
    if (
      aminoAcids.toUpperCase() !== residues.slice(part.start, end).toUpperCase() ||
      antibodyNumberingInfo.numbering.length !== end - part.start
    ) {
      return [];
    }
    return [
      {
        start: part.start,
        labels: antibodyNumberingInfo.numbering,
        summary: getNumberingSummary(antibodyNumberingInfo),
        sourceName: sequence.displayLabel,
      },
    ];
  });
}

/**
 * Returns the antibody numbering stored on an AA sequence version or, if it has none, the numbering stored
 * on its parts. Returns no numberings while they load.
 */
export function useStoredResidueNumberings({
  aaSequenceId,
  aaSequenceVersionId,
  parts,
  residues,
  skip = false,
}: {
  aaSequenceId: string;
  aaSequenceVersionId: number;
  parts: ReadonlyArray<AaSequencePart>;
  residues: string;
  skip?: boolean;
}): Array<ResidueNumbering> {
  const {loaded: ownNumberingLoaded, numbering: ownNumbering} = useVersionNumbering({
    aaSequenceId,
    aaSequenceVersionId,
    residues,
    skip,
  });
  const partsResult = useSkippableBenchlingQuery<
    AaSequencePartsAntibodyNumberingQuery,
    AaSequencePartsAntibodyNumberingQueryVariables
  >(AA_SEQUENCE_PARTS_ANTIBODY_NUMBERING_QUERY, {
    skip: skip || parts.length === 0 || !ownNumberingLoaded || ownNumbering != null,
    variables: {ids: [...new Set(parts.map(({proteinId}) => proteinId))]},
  });
  if (ownNumbering != null) {
    return [ownNumbering];
  }
  return partsResult.type === QueryResultType.SUCCESS
    ? getPartResidueNumberings(partsResult.data, parts, residues)
    : [];
}

/**
 * Lays out the numberings with residues on the slice from sliceStart to sliceEnd (exclusive), so that
 * numberings on the same layer don't overlap. Returns each numbering on the slice with its layer, and the
 * number of layers.
 */
export function layOutSliceNumberings(
  numberings: ReadonlyArray<ResidueNumbering>,
  sequenceLength: number,
  sliceStart: number,
  sliceEnd: number
): {layers: Array<{numbering: ResidueNumbering; layer: number}>; layerCount: number} {
  const sliceModels = numberings
    .map((numbering, index) => ({
      index,
      start: numbering.start,
      end: numbering.start + numbering.labels.length,
      numbering,
    }))
    .filter(({start, end}) => start < sliceEnd && sliceStart < end);
  const {max, layers} = getFeatureLayersForRegionModels(
    sliceModels,
    sequenceLength,
    ({index}) => String(index),
    false
  );
  return {
    layers: layers.map(({model, layer}) => ({numbering: model.numbering, layer})),
    // max is the index of the last layer, and -1 when there are none
    layerCount: max + 1,
  };
}

export interface Props {
  aaSequenceId: string;
  aaSequenceVersionId: number;
  layout: AntibodyNumberingLayout;
  // The parts of the version, whose stored numbering is shown if the version has none of its own
  parts: ReadonlyArray<AaSequencePart>;
  redraw: (boxHeight: number) => void;
  residues: string;
  setSelection: (start: number, end: number) => void;
  sliceStart: number;
  // Exclusive
  sliceEnd: number;
  // A function that takes the index of a residue and returns its x-axis position on the slice
  xFn: (residueIndex: number) => number;
}

/**
 * Displays the antibody numbering stored on an AA sequence version, or on its parts if it has none, on its
 * sequence map. Numberings that overlap are drawn on separate layers.
 */
export default function StoredAntibodyNumberingSlice({
  aaSequenceId,
  aaSequenceVersionId,
  layout,
  parts,
  redraw,
  residues,
  setSelection,
  sliceStart,
  sliceEnd,
  xFn,
}: Props): JSX.Element | null {
  const numberings = useStoredResidueNumberings({aaSequenceId, aaSequenceVersionId, parts, residues});
  const {layers, layerCount} = layOutSliceNumberings(numberings, residues.length, sliceStart, sliceEnd);

  const layerHeight = getNumberingLayerHeight(xFn(sliceStart + 1) - xFn(sliceStart));
  const boxHeight = layerCount > 0 ? layerCount * layerHeight + layout.vMargin : 0;
  useEffect(() => {
    redraw(boxHeight);
  }, [boxHeight, redraw]);

  if (layers.length === 0) {
    return null;
  }

  return (
    <AntibodyNumberingTrack
      numberings={layers.map(({numbering}) => numbering)}
      onAlignment={false}
      yPositions={layers.map(({layer}) => layer * layerHeight)}
      {...{layout, residues, setSelection, sliceStart, sliceEnd, xFn}}
    />
  );
}
